<script setup lang="ts">
import { useWindowScroll, watchDebounced, watchThrottled } from '@vueuse/core';
import { vElementVisibility } from '@vueuse/components';
import type { DynamicInputData, LinkNode } from '@laam/cms-shared';
import { EntityCard } from '@laam/ui/cms';
import type { ComponentPublicInstance } from 'vue';

type Props = {
	dataSource: {
		dataSource: string;
		clickedFrom: string;
	};
	componentInfo?: {
		activeTab?: string;
		heading?: string;
		component?: string;
		input?: DynamicInputData;
		method: string;
	};
	horizontalIndex: number;
	verticalIndex: number;
	entity: string;
	data: LinkNode;
	shape: 'square' | 'rectangle';
};

const { $cathodeClient, $eventClient } = useNuxtApp();

const props = defineProps<Props>();
const elementRef = ref<ComponentPublicInstance>();

const { y } = useWindowScroll();
const hasWindowScrolled = ref(false);
watchThrottled(
	y,
	() => {
		hasWindowScrolled.value = true;
	},
	{ throttle: 200 },
);

function handleClickEvent() {
	$cathodeClient.queueEvent('entity_click', {
		source: window.location.href,
		page: 'homepage',
		href: props.data.href || '',
		clicked_from: props.dataSource.clickedFrom as 'homepage',
		image: props.data.image || '',
		title: props.data.name,
		entity: props.entity as '',
		horizontal_index: props.horizontalIndex,
		input: props.componentInfo?.input,
		vertical_index: props.verticalIndex,
		slotted: props.data.slotted ?? false,
		heading: props.componentInfo?.heading || '',
		component: props.componentInfo?.component || '',
		product_count: props.data.product_count!,
		sale_percentage: props.data.max_sale!,
		scroll_position: y.value,
		data_source: '',
		data_string: JSON.stringify(props.data),
		active_tab: props.componentInfo?.activeTab,
	});
	$eventClient.sendEvent('homepage-entity-click', {
		to: props.data.href,
		index: props.verticalIndex,
		entity: props.entity,
		component: props.componentInfo?.component || '',
		heading: props.componentInfo?.heading || '',
		title: props.data.name,
		horizontalIndex: props.horizontalIndex,
		url: window.location.href,
	});
}

const isVisible = ref(false);
function onElementVisibility(state: boolean) {
	isVisible.value = state;
}

watchDebounced(
	isVisible,
	(value) => {
		if (value) {
			$cathodeClient.queueImpression('entity_impression', {
				data: [
					{
						href: props.data.href,
						product_count: props.data.product_count,
						sale_percentage: props.data.max_sale,
						slotted: props.data.slotted,
						horizontal_index: props.horizontalIndex,
						data_string: JSON.stringify(props.data),
						image: props.data.image,
						title: props.data.name,
					},
				],
				method: props.componentInfo?.method!,
				input: props.componentInfo?.input,
				vertical_index: props.verticalIndex,
				entity: props.entity as 'brand',
				heading: props.componentInfo?.heading || '',
				component: props.componentInfo?.component || '',
				scroll_position: y.value,
				source: window.location.href,
				type: hasWindowScrolled.value ? 'scroll_impression' : 'load_impression',
				time: new Date().toUTCString(),
				data_source: '',
				clicked_from: '',
				page: 'homepage',
				active_tab: props.componentInfo?.activeTab,
			});
		}
	},
	{
		debounce: useRuntimeConfig().public.impressionDebounceTime,
	},
);

onMounted(() => {
	nextTick(() => {
		if (!elementRef.value) return;
		const element = elementRef.value.$el as HTMLElement;
		isVisible.value = isElementVisible(element);
	});
});
</script>

<template>
	<NuxtLink
		ref="elementRef"
		v-element-visibility="[onElementVisibility, { threshold: 0.8 }]"
		:to="data.href"
		@click="handleClickEvent()"
	>
		<EntityCard
			:data="data"
			:entity="entity"
			:index="horizontalIndex"
			:shape="shape"
		/>
	</NuxtLink>
</template>
